import { GridColDef, GridRowsProp, GridSortDirection } from '@mui/x-data-grid-pro';
import { renderCellTooltip } from 'components/grid/cell/GridCellTooltip';
import { renderSampleCommentCellTooltip } from 'components/grid/cell/SampleCommentGridCellTooltip';
import { useEffect, useMemo, useState } from 'react';
import useAuth from 'auth/UseAuth';
import { renderGuidCellTooltip } from 'components/grid/cell/GuidGridCellTooltip';
import { CompactGridWrapper } from 'components/grid/CompactGridWrapper';
import {
  GetDiseaseAreaPatientRecordsByBiobank,
  GetPathogenGroupPatientRecordsByLab,
  GetPatientRecords,
  GetPatientRecordsBySampleType,
  PatientRecord,
  PatientRecordOrderBy,
} from 'data/PatientRecordData';
import { ItemOfResearch } from 'data/ResearchProjectData';
import { PatientCountType } from 'components/grid/GridCountType';
import { FlexTableBox } from '../components/FlexTableBox';
import { ErrorManagement, LoadingState } from '../components/LoadingStateUtil';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { ErrorIndicator } from '../components/ErrorIndicator';
import useMemoTranslation from '../hooks/UseMemoTranslation';
import {
  bbid,
  bbids,
  biobank,
  biobanks,
  deidentity,
  ethnicity,
  foreignHash,
  foreignHashes,
  gender,
  icd9AndIcd10Codes,
  lab,
  labs,
  latestSampleCollectionDate,
  patientId,
  race,
  sampleComment,
  sampleComments,
  sampleCount,
  sampleType,
  sampleTypes,
  snomedConceptIdsWithCtValue,
  yearOfBirth,
} from '../util/Constants';
import { TimeInterval } from 'data/ResearchProjectPatientData';
import { upperFirst } from 'lodash';
import { ConvertToSingleValue } from 'data/conversions/StringConversions';

export interface Patient {
  patientId: string;
}

export interface PatientRecordGridProps {
  itemOfResearch: ItemOfResearch;
  timeInterval: TimeInterval;
  biobankId?: string;
  labId?: string;
  sampleTypeId?: string;
  numRecords: number;
  decrypted: boolean;
  countType: PatientCountType;
}

export const PatientRecordGrid = ({
  itemOfResearch,
  timeInterval,
  biobankId,
  labId,
  sampleTypeId,
  numRecords,
  decrypted,
  countType,
}: PatientRecordGridProps) => {
  const { accessToken } = useAuth();

  const [loadingState, setLoadingState] = useState<LoadingState>({ status: 'NotStarted' });
  const [orderBy, setOrderBy] = useState<PatientRecordOrderBy>('PatientId');
  const [sortOrder, setSortOrder] = useState<GridSortDirection>('asc');
  const [pageSize, setPageSize] = useState<number>(100);
  const [pageStart, setPageStart] = useState<number>(0);
  const [patientRecords, setPatientRecords] = useState<PatientRecord[]>();
  const [rows, setRows] = useState<GridRowsProp>([]);

  const columns = useColumns();

  useEffect(() => {
    ErrorManagement('Loading', setLoadingState, async () => {
      if (!accessToken || !sortOrder) {
        setLoadingState({ status: 'Error' });
        return;
      }

      if (itemOfResearch.type === 'DiseaseArea' && 'byBiobank' && biobankId !== undefined) {
        setPatientRecords(
          await GetDiseaseAreaPatientRecordsByBiobank(
            itemOfResearch.id,
            biobankId,
            orderBy,
            sortOrder,
            pageSize,
            pageStart,
            decrypted,
            accessToken
          )
        );
      } else if (itemOfResearch.type === 'PathogenGroup' && 'byLab' && labId !== undefined) {
        setPatientRecords(
          await GetPathogenGroupPatientRecordsByLab(
            itemOfResearch.id,
            timeInterval,
            labId,
            orderBy,
            sortOrder,
            pageSize,
            pageStart,
            decrypted,
            accessToken
          )
        );
      } else if (countType === 'bySampleType' && sampleTypeId !== undefined) {
        setPatientRecords(
          await GetPatientRecordsBySampleType(
            itemOfResearch.id,
            itemOfResearch.type,
            timeInterval,
            sampleTypeId,
            orderBy,
            sortOrder,
            pageSize,
            pageStart,
            decrypted,
            accessToken
          )
        );
      } else if (countType === 'all') {
        setPatientRecords(
          await GetPatientRecords(
            itemOfResearch.id,
            itemOfResearch.type,
            timeInterval,
            orderBy,
            sortOrder,
            pageSize,
            pageStart,
            decrypted,
            accessToken
          )
        );
      }

      setLoadingState({ status: 'Complete' });
    });
  }, [
    numRecords,
    biobankId,
    sampleTypeId,
    orderBy,
    sortOrder,
    pageSize,
    pageStart,
    decrypted,
    countType,
    accessToken,
    labId,
    itemOfResearch.type,
    itemOfResearch.id,
    timeInterval,
  ]);

  useEffect(() => {
    if (patientRecords) {
      setRows(getRows(patientRecords));
    }
  }, [patientRecords, decrypted]);

  return (
    <FlexTableBox>
      <CompactGridWrapper
        rows={rows}
        columns={columns}
        disableColumnFilter={true}
        pagination={true}
        paginationMode='server'
        onPaginationModelChange={model => {
          setPageStart(model.page);
          setPageSize(model.pageSize);
        }}
        paginationModel={{ pageSize: pageSize, page: pageStart }}
        rowCount={numRecords}
        pageSizeOptions={[100, 250, 500]}
        sortingMode='server'
        sortModel={[{ field: orderBy, sort: sortOrder }]}
        onSortModelChange={sortModel => {
          setOrderBy(sortModel.length > 0 ? (upperFirst(sortModel[0].field) as PatientRecordOrderBy) : 'PatientId');
          setSortOrder(sortModel.length > 0 ? sortModel[0].sort : 'asc');
        }}
        density='compact'
        hideFooterSelectedRowCount={true}
      />
      <LoadingIndicator type={'Linear'} loadingState={loadingState} margin={'T'} />
      <ErrorIndicator loadingState={loadingState} />
    </FlexTableBox>
  );
};

function getRows(patientRecords: PatientRecord[]) {
  const rows: any[] = [];

  patientRecords.forEach((patient, index) => {
    rows.push({
      id: index++,
      patientId: patient.patientId,
      deidentity: patient.deidentity,
      sampleCount: patient.sampleCount,
      labs: ConvertToSingleValue(patient.sampleCount, patient.labs),
      biobanks: ConvertToSingleValue(patient.sampleCount, patient.biobanks),
      bbids: ConvertToSingleValue(patient.sampleCount, patient.bbids),
      foreignHashes: ConvertToSingleValue(patient.sampleCount, patient.foreignHashes),
      sampleTypes: ConvertToSingleValue(patient.sampleCount, patient.sampleTypes),
      sampleComments: ConvertToSingleValue(patient.sampleCount, patient.sampleComments),
      latestSampleCollectionDate: dateToString(new Date(patient.latestSampleCollectionDate)),
      gender: patient.gender,
      yearOfBirth: patient.yearOfBirth,
      race: patient.race,
      ethnicity: patient.ethnicity,
      icd9AndIcd10Codes: patient.icd9AndIcd10Codes.sort().join(', '),
      snomedConceptIdsWithCtValue: patient.snomedConceptIds.sort().join(', '),
    });
  });
  return rows;
}

function dateToString(date: Date) {
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

const useColumns = (): GridColDef[] => {
  const { t } = useMemoTranslation();

  return useMemo(
    () => [
      {
        field: patientId,
        headerName: t(patientId),
        width: 130,
        align: 'left',
        renderCell: renderGuidCellTooltip,
      },
      {
        field: deidentity,
        headerName: t(deidentity),
        width: 130,
        align: 'left',
        renderCell: renderGuidCellTooltip,
      },
      {
        field: sampleCount,
        headerName: t(sampleCount),
        headerAlign: 'center',
        align: 'center',
        width: 150,
      },
      {
        field: biobanks,
        headerName: t(biobank),
        width: 150,
        renderCell: renderCellTooltip,
      },
      {
        field: labs,
        headerName: t(lab),
        width: 150,
        renderCell: renderCellTooltip,
      },
      {
        field: bbids,
        headerName: t(bbid),
        width: 150,
        renderCell: renderCellTooltip,
      },
      {
        field: foreignHashes,
        headerName: t(foreignHash),
        width: 180,
        type: 'string',
        renderCell: renderCellTooltip,
      },
      {
        field: sampleTypes,
        headerName: t(sampleType),
        width: 200,
        renderCell: renderCellTooltip,
      },
      {
        field: sampleComments,
        headerName: t(sampleComment),
        width: 230,
        renderCell: renderSampleCommentCellTooltip,
      },
      {
        field: latestSampleCollectionDate,
        headerName: t(latestSampleCollectionDate),
        headerAlign: 'center',
        align: 'center',
        width: 230,
      },
      { field: gender, headerName: t(gender), width: 100 },
      { field: yearOfBirth, headerName: t(yearOfBirth), headerAlign: 'center', align: 'center', width: 120 },
      { field: race, headerName: t(race), width: 170 },
      { field: ethnicity, headerName: t(ethnicity), width: 170 },
      {
        field: icd9AndIcd10Codes,
        headerName: t(icd9AndIcd10Codes),
        width: 230,
        renderCell: renderCellTooltip,
      },
      {
        field: snomedConceptIdsWithCtValue,
        headerName: t(snomedConceptIdsWithCtValue),
        width: 230,
        renderCell: renderCellTooltip,
      },
    ],
    [t]
  );
};
